import { useEffect, useState, useCallback, useRef } from "react";
import api, { errText } from "@/lib/api";
import { TierBadge } from "@/components/TierBadge";
import { Pagination } from "@/components/Pagination";
import { toast } from "sonner";
import {
  Loader2, Send, Upload, FileUp, Users2, UserCheck, Trash2, Search, AlertCircle, ClipboardPaste, FileSpreadsheet,
} from "lucide-react";

const STATUS_STYLE = {
  New: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Connection Sent": "bg-blue-50 text-klein border-blue-200",
  Converted: "bg-zinc-900 text-white border-black",
};

export default function ManualProspecting() {
  const [reps, setReps] = useState([]);
  const [leadgen, setLeadgen] = useState("");
  const [mode, setMode] = useState("file"); // file | paste
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);   // global queue stats (remaining per rep)
  const [mstats, setMstats] = useState({});   // manual-only totals
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [repFilter, setRepFilter] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, pages: 1, limit: 25 });
  const fileRef = useRef();

  const loadStats = useCallback(() => {
    api.get("/prospects/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/prospects-manual/stats").then((r) => setMstats(r.data.totals || {})).catch(() => {});
  }, []);
  const loadItems = useCallback(() => {
    const params = { sort: sortBy, page, limit: 25 };
    if (filter) params.status = filter;
    if (repFilter) params.assigned_leadgen = repFilter;
    if (searchQ) params.search = searchQ;
    api.get("/prospects-manual/list", { params }).then((r) => {
      setItems(r.data.items);
      setMeta({ total: r.data.total, pages: r.data.pages, limit: r.data.limit });
    }).catch(() => {});
  }, [filter, repFilter, searchQ, sortBy, page]);

  useEffect(() => { api.get("/team/leadgens").then((r) => setReps(r.data)).catch(() => {}); }, []);
  useEffect(() => { setPage(1); }, [filter, repFilter, searchQ, sortBy]);
  useEffect(() => { loadStats(); loadItems(); }, [loadStats, loadItems]);

  const linkCount = (text.match(/linkedin\.com\/in\//gi) || []).length;

  const doImport = async () => {
    if (!leadgen) { toast.error("Lead-gen rep select karo"); return; }
    setImporting(true);
    try {
      let data;
      if (mode === "paste") {
        if (!text.trim()) { toast.error("Pehle data paste karo"); setImporting(false); return; }
        ({ data } = await api.post("/prospects-manual/import-text", { text, assigned_leadgen: leadgen }));
      } else {
        if (!file) { toast.error("Excel / CSV file select karo"); setImporting(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("assigned_leadgen", leadgen);
        ({ data } = await api.post("/prospects-manual/import-file", fd, { headers: { "Content-Type": "multipart/form-data" } }));
      }
      setResult(data);
      toast.success(`${data.created} lead(s) ${data.assigned_to} ki queue me gayi${data.duplicates ? ` · ${data.duplicates} duplicate skip` : ""}${data.invalid ? ` · ${data.invalid} invalid` : ""}`);
      setText(""); setFile(null); if (fileRef.current) fileRef.current.value = "";
      loadStats(); loadItems();
    } catch (e) { toast.error(errText(e)); } finally { setImporting(false); }
  };

  const del = async (p) => {
    try { await api.delete(`/prospects/${p.id}`); loadStats(); loadItems(); toast.success("Deleted"); }
    catch (e) { toast.error(errText(e)); }
  };

  const lowReps = (stats?.per_rep || []).filter((r) => r.leadgen_id && r.remaining < 5);
  const STATUSES = ["", "New", "Connection Sent", "Converted"];

  return (
    <div className="p-6 max-w-6xl">
      <h1 className="font-head text-2xl font-black tracking-tight">Prospecting Manual</h1>
      <p className="text-xs text-zinc-500 font-mono mb-5">Excel / paste → seedha rep ki queue me · koi enrichment ya score check nahi · baaki flow same</p>

      {lowReps.length > 0 && (
        <div className="mb-4 border-2 border-red-300 bg-red-50 rounded-xl px-4 py-3 flex items-start gap-3" data-testid="manual-low-queue-alert">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">
            <span className="font-bold">Top up needed —</span> {lowReps.map((r) => `${r.leadgen_name} (${r.remaining} left)`).join(", ")}.
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5 mb-6">
        <div className="bg-white border-2 border-black rounded-xl p-4" data-testid="manual-import-panel">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-3"><Upload className="h-4 w-4" /> Add leads to a rep's queue</div>
          <select data-testid="manual-assign-leadgen" value={leadgen} onChange={(e) => setLeadgen(e.target.value)}
            className="w-full border border-zinc-300 rounded-lg text-sm px-2 py-2 mb-2 focus:border-klein focus:outline-none">
            <option value="">Assign to lead-gen rep…</option>
            {reps.map((r) => <option key={r.id} value={r.id}>{r.name}{r.assigned_bde ? "" : " (no BDE linked)"}</option>)}
          </select>

          <div className="flex gap-1 bg-zinc-100 rounded-xl p-1 w-fit mb-2">
            {[["file", "Upload Excel", FileSpreadsheet], ["paste", "Paste data", ClipboardPaste]].map(([v, l, Icon]) => (
              <button key={v} data-testid={`manual-mode-${v}`} onClick={() => setMode(v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-lg transition-all ${mode === v ? "bg-white text-klein shadow-sm" : "text-zinc-500 hover:text-zinc-900"}`}>
                <Icon className="h-3.5 w-3.5" /> {l}
              </button>
            ))}
          </div>

          {mode === "paste" ? (
            <>
              <textarea data-testid="manual-paste" value={text} onChange={(e) => setText(e.target.value)} rows={7}
                placeholder={"Ek ke niche ek paste karo:\n\nLink\nhttps://linkedin.com/in/...\n\nFirst name\nRahul\n\nLast name\nSharma\n\nPosition\nFounder at Acme"}
                className="w-full border border-zinc-300 rounded-lg p-2.5 text-xs font-mono resize-none focus:border-klein focus:outline-none" />
              <div className="text-[11px] text-zinc-400 font-mono mt-1">{linkCount} LinkedIn link(s) detected</div>
            </>
          ) : (
            <div className="border-2 border-dashed border-zinc-300 rounded-lg p-5 text-center">
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv,.tsv,.txt" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="manual-file-input" />
              <button data-testid="manual-pick-file" onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-1.5 border border-zinc-300 rounded-lg text-sm font-medium px-3 py-2 hover:border-klein hover:text-klein transition-colors">
                <FileUp className="h-4 w-4" /> {file ? "Change file" : "Choose Excel / CSV"}
              </button>
              <div className="text-xs font-mono text-zinc-500 mt-2">{file ? file.name : "Columns: Link, First name, Last name, Position"}</div>
            </div>
          )}

          <button data-testid="manual-import-btn" onClick={doImport} disabled={importing}
            className="w-full mt-3 bg-klein hover:bg-klein-hover text-white text-sm font-medium px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to Queue
          </button>
          <p className="text-[11px] text-zinc-400 mt-2">Header kisi bhi case me chalega (LINK / link / First Name…). LinkedIn id se duplicate apne aap skip hote hain.</p>

          {result && (
            <div className="mt-3 border border-zinc-200 bg-zinc-50 rounded-lg p-3 text-xs font-mono space-y-0.5" data-testid="manual-result">
              <div className="text-emerald-700 font-bold">{result.created} added → {result.assigned_to}</div>
              <div className="text-zinc-600">{result.duplicates} already in system · {result.dup_in_input} repeat in file · {result.invalid} invalid link · {result.total} rows read</div>
              {(result.warnings || []).map((w, i) => <div key={i} className="text-amber-700">⚠ {w}</div>)}
            </div>
          )}
        </div>

        <div className="bg-white border border-zinc-300 rounded-xl p-4" data-testid="manual-remaining-panel">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-3"><Users2 className="h-4 w-4" /> Remaining in each rep's queue</div>
          <div className="space-y-2">
            {(stats?.per_rep || []).map((r) => (
              <div key={r.leadgen_id || "none"} className="flex items-center justify-between border-b border-zinc-100 pb-2 last:border-0">
                <div className="flex items-center gap-2 text-sm"><UserCheck className="h-4 w-4 text-zinc-400" /> {r.leadgen_name}</div>
                <div className="flex items-center gap-3 text-xs font-mono">
                  <span className="text-emerald-700 font-bold">{r.counts?.New || 0} to contact</span>
                  <span className="text-klein">{r.counts?.["Connection Sent"] || 0} sent</span>
                  <span className={`font-bold ${r.leadgen_id && r.remaining < 5 ? "text-red-600" : "text-zinc-700"}`}>{r.remaining} left</span>
                </div>
              </div>
            ))}
            {(stats?.per_rep || []).length === 0 && <div className="text-xs text-zinc-400 font-mono py-4 text-center">No prospects yet.</div>}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-3">
        {["New", "Connection Sent", "Converted"].map((s) => (
          <span key={s} className={`text-[11px] font-mono px-2 py-1 rounded-md border ${STATUS_STYLE[s]}`}>{s}: {mstats[s] || 0}</span>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-zinc-400" />
          <input data-testid="manual-search" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Search name / company / URL"
            className="pl-9 pr-3 py-2 border border-zinc-300 rounded-lg text-sm w-56 focus:border-klein focus:outline-none" />
        </div>
        <select data-testid="manual-rep-filter" value={repFilter} onChange={(e) => setRepFilter(e.target.value)} className="border border-zinc-300 rounded-lg text-sm px-2 py-1.5 focus:border-klein focus:outline-none">
          <option value="">All reps</option>
          {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <select data-testid="manual-status-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="border border-zinc-300 rounded-lg text-sm px-2 py-1.5 focus:border-klein focus:outline-none">
          {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
        <select data-testid="manual-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="border border-zinc-300 rounded-lg text-sm px-2 py-1.5 focus:border-klein focus:outline-none">
          <option value="recent">Sort: recent</option>
          <option value="name">Sort: name</option>
        </select>
        <span className="text-xs text-zinc-400 font-mono ml-auto">{meta.total} total</span>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid="manual-prospect-list">
        {items.map((p) => (
          <div key={p.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-100 last:border-0" data-testid={`manual-prospect-${p.id}`}>
            <TierBadge tier={p.tier} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{p.name || <span className="text-zinc-400">No name</span>}</div>
              <div className="text-[11px] text-zinc-500 truncate">{p.headline || "—"}</div>
              <div className="text-[11px] text-zinc-400 truncate font-mono">{p.linkedin_url}</div>
            </div>
            <span className="text-[11px] text-zinc-500 truncate max-w-[120px]">{p.assigned_leadgen_name || "—"}</span>
            <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-md border ${STATUS_STYLE[p.status] || ""}`}>{p.status}</span>
            <button data-testid={`manual-delete-${p.id}`} onClick={() => del(p)} className="text-zinc-400 hover:text-red-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {items.length === 0 && <div className="text-center text-zinc-400 font-mono py-12 text-xs">Abhi koi manual prospect nahi.</div>}
      </div>
      <Pagination page={page} pages={meta.pages} total={meta.total} limit={meta.limit} onPage={setPage} />
    </div>
  );
}